/* cspell:words foia xpress */

import {
  all,
  call,
  fork,
  put,
  takeEvery,
  delay,
  race,
  take,
  select,
} from 'typed-redux-saga/macro';
import { TDOId } from '@common-modules/universal/models/Brands';
import { modules } from '@veritone/glc-redux';
import { enqueueSnackbar } from '@common-modules/snackbar';

import {
  CREATE_ENGINE_JOB_SUCCESS,
  PROCESS_ENGINE_REQUEST_FAILURE,
  PROCESS_ENGINE_REQUEST_SUCCESS,
  processEngineRequestAction,
} from '@common-modules/engines/ingestion';
import { isPdfMedia } from '@common-modules/engines/ingestion/services';
import { isDocumentFile } from '@helpers/filePickerSelection';
import { ProcessEngineRequest } from '@common-modules/engines/ingestion/models/services';
import {
  actionFetchOrgTDODetail,
  actionMarkOrgTdoNew,
  actionMarkOrgTdoRecorded,
} from '@redact-modules/mainPage/actions';

import { CREATE_TDO, CREATE_TDO_FAILURE, CREATE_TDO_SUCCESS } from './actions';
import {
  fetchPdfPageCount,
  serviceCreateOriginalMediaAsset,
  serviceCreatePdfMediaAsset,
  serviceCreateTDO,
  serviceFinalizePdfTdo,
  serviceHasOriginalMediaAsset,
} from './services';
import { GlobalSettings } from '@common/state/modules/mediaDetails/models';
import { selectProfileList } from '../settingsProfile/selectors';
import { selectFeatureFlags } from '@common/user-permissions';
import * as foiaXpressAPI from '@helpers/foiaXpressAPI';
import {
  actionMarkTdoRecorded,
  actionSetSelectedFolder,
} from '../casemedia/actions';
import { RECORD_UPLOAD_LEXICON_SELECTION } from '../documents/actions';
import { selectSelectedVirtualFolder } from '../casemedia/selectors';
import { VirtualFolderStateType } from '../casemedia/store';

const { auth: authModule, config: configModule } = modules;

interface GraphQLConfig {
  apiRoot: string;
  graphQLEndpoint: string;
  veritoneAppId: string;
}

function selectToken(state: any): string {
  return (
    authModule.selectOAuthToken(state) ||
    authModule.selectSessionToken(state) ||
    ''
  );
}

function selectGraphQLConfig(state: any): GraphQLConfig {
  const cfg = configModule.getConfig(state) as Partial<GraphQLConfig>;
  return {
    apiRoot: cfg.apiRoot ?? '',
    graphQLEndpoint: cfg.graphQLEndpoint ?? '',
    veritoneAppId: cfg.veritoneAppId ?? '',
  };
}

const PDF_CONTENT_TYPE = 'application/pdf';

/**
 * Which uploads the app owns the `original-media` write for and drops `url`
 * from the engine payload for, under VE-30513's revised design: **A/V,
 * image, and the browser's untyped `binary/octet-stream` fallback** (for
 * files it could not sniff — `.mkv`, `.mxf`, `.m2ts`, …).
 *
 * Documents are DELIBERATELY excluded. The document pipeline is
 * PDF-converter → OCR → PII, each engine reads `payload.url` directly to
 * download the input (`services.ts:566-574`), so dropping `url` there would
 * leave the PDF converter with no source. The document branch keeps the old
 * best-effort shape — attach the `.docx` as `original-media`, keep `url` in
 * the engine payload.
 *
 * PDF is excluded for a different reason: the fast-upload path attaches the
 * uploaded file as the `media` asset directly, so there is no separate
 * `original-media` to write.
 *
 * The gate rests on `unsignedUrl` presence at the call site (set only by the
 * app's own `getSignedWritableUrl` flow): Ingest-from-URL has no `fileType`
 * either, so a `shouldAttachOriginalMedia(undefined, …)` miss covers it.
 */
function shouldAttachOriginalMedia(
  fileType: string | undefined | null,
  fileName: string
): boolean {
  if (!fileType) {
    return false;
  }
  if (isPdfMedia(fileType, fileName)) {
    return false;
  }
  if (isDocumentFile(fileType, fileName)) {
    return false;
  }
  const normalized = fileType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (!normalized) {
    return false;
  }
  return (
    normalized.startsWith('video/') ||
    normalized.startsWith('audio/') ||
    normalized.startsWith('image/') ||
    normalized === 'binary/octet-stream'
  );
}

/**
 * Milliseconds a single `createAsset` call is given before the saga gives up
 * on it and reads back to decide whether to retry. Matches the existing
 * ingestion job-wait window below.
 */
const ATTACH_TIMEOUT_MS = 10_000;

/**
 * Attach the uploaded file as the TDO's `original-media` asset and CONFIRM
 * the write landed before telling the caller.
 *
 * Why confirm-or-fail replaced the first PR's best-effort write (VE-30513,
 * revised design per Derrick): once we drop `url` from the engine payload
 * (see `handleCreateTDOSuccess` below), the ingester **depends** on this
 * asset existing to find the file. A silent failure would succeed-into-a-
 * time-bomb — the engine request itself is accepted because job creation
 * succeeds; the task then dies inside the engine minutes later with
 * `IngestionError("payload url is null/None")`, surfacing as a tile that
 * fails long after the user has moved on.
 *
 * `fetchGraphQLApi` has no `AbortController`, no timeout, no retry
 * (`callApi.ts`), and this call sits between `CREATE_TDO_SUCCESS` and the
 * engine-request `put`. A stalled server would hang the saga indefinitely.
 * The race here bounds the wait; the read-back disambiguates ambiguity:
 * `race` cancels the SAGA BRANCH, not the in-flight fetch, so a timed-out
 * `createAsset` may still land on the server, and `createAsset` is not
 * idempotent. Retrying blindly would duplicate the record. Looking first
 * never does.
 *
 * The engine request has not been dispatched when this runs, on a TDO
 * `createTDO` has only just returned, so the ingester cannot have written
 * anything yet and no other path can have touched it: any `original-media`
 * on this TDO is necessarily ours. That is why the read-back tests for
 * PRESENCE rather than matching our `uri` — see
 * `serviceHasOriginalMediaAsset`, where comparing the URI could only
 * produce a false miss and so manufacture the duplicate this loop exists
 * to avoid.
 *
 * Returns `true` iff a matching record is present on the server. The
 * caller then skips `url` in the engine payload; a `false` return means
 * the engine request must not be dispatched (there is nothing for the
 * ingester to fall back to).
 */
function* attachOriginalMedia({
  tdoId,
  uri,
  contentType,
  fileName,
  token,
  cfg,
}: {
  tdoId: TDOId;
  uri: string;
  contentType: string;
  fileName: string;
  token: string;
  cfg: GraphQLConfig;
}): Generator<unknown, boolean, unknown> {
  for (const attempt of [1, 2]) {
    try {
      const outcome = yield* race({
        ok: call(
          serviceCreateOriginalMediaAsset,
          tdoId,
          uri,
          contentType,
          fileName,
          token,
          cfg
        ),
        timeout: delay(ATTACH_TIMEOUT_MS),
      });
      if (outcome.ok) {
        return true;
      }
      // Timed out. Fall through to the read-back.
    } catch (e) {
      // Transport or server error on the write itself. The read-back below
      // still runs — the server may have accepted the write and then thrown
      // on the response path, which `createAsset` is particularly vulnerable
      // to since it is not idempotent.
      console.warn(
        `[uploads] original-media attach attempt ${attempt} threw`,
        e
      );
    }
    try {
      const existingId = yield* call(
        serviceHasOriginalMediaAsset,
        tdoId,
        token,
        cfg
      );
      if (existingId) {
        return true;
      }
    } catch (e) {
      // Read-back failure is treated as a miss: let the retry try the write
      // again. A second write + a second read-back is the worst case.
      console.warn(
        `[uploads] original-media read-back attempt ${attempt} threw`,
        e
      );
    }
  }
  console.error(
    `[uploads] original-media attach for TDO ${tdoId} not confirmed after 2 attempts; engine request dropped`
  );
  return false;
}

export function* uploadsSaga() {
  yield* all([
    fork(onActionCreateTDO),
    fork(onActionCreateTDOSuccess),
    fork(onActionCreateTDOFailure),
    fork(onActionIngestionComplete),
    fork(onActionIngestionFailure),
  ]);
}

/**
 * Fast-path setup for a freshly created PDF TDO (VE-23391): skip the PDF
 * converter by attaching the uploaded file as the `media` asset, recording its
 * page count + GLC metadata on `veritoneFile`, and flipping the TDO to
 * `recorded`. The `media` asset references the already-uploaded S3 object by
 * its unsigned URL — the signed `getUrl` expires, and this reference is
 * permanent, so the caller refuses the upload rather than substituting one.
 * `getUrl` is used only to read the page count, which happens now.
 *
 * A failure here stops the upload: it is surfaced as a snackbar and no engine
 * request is made.
 */
function* setupPdfFastUpload({
  tdoId,
  fileName,
  uri,
  pageCountUrl,
}: {
  tdoId: TDOId;
  fileName: string;
  uri: string;
  pageCountUrl: string;
}) {
  const token = yield* select(selectToken);
  const gqlConfig = yield* select(selectGraphQLConfig);

  const assetId = yield* call(
    serviceCreatePdfMediaAsset,
    tdoId,
    uri,
    PDF_CONTENT_TYPE,
    token,
    gqlConfig
  );

  const pageCount = yield* call(fetchPdfPageCount, pageCountUrl);

  yield* call(
    serviceFinalizePdfTdo,
    tdoId,
    assetId,
    { fileName, fileType: PDF_CONTENT_TYPE, pageCount },
    token,
    gqlConfig
  );
}

/**
 * Re-fetch the selected folder so a file whose server state just changed picks
 * up that change in the folder view. Used right after the PDF fast path flips a
 * TDO to `recorded` (so the row stops reading "Ingesting Media", becomes
 * openable, and — once the PII job starts — shows "Cognition Processing"), and
 * again on ingestion/cognition completion. No-op when the file isn't in the
 * currently-selected folder.
 */
export function* refreshSelectedFolderForTdo(tdoId: TDOId) {
  const selectedVirtualFolder: VirtualFolderStateType = yield* select(
    selectSelectedVirtualFolder
  );
  const inSelectedVirtualFolder =
    selectedVirtualFolder.parent?.caseFiles?.inputFiles?.find(
      (file) => file.id === tdoId
    );
  if (selectedVirtualFolder.parent && inSelectedVirtualFolder) {
    yield* put(
      actionSetSelectedFolder({
        folder: selectedVirtualFolder.parent,
        isCase: true,
      })
    );
  }
}

/** How long to wait for the ingestion job before re-fetching anyway. */
const JOB_CREATE_WAIT_MS = 10_000;

/**
 * The ingestion request for one TDO has been answered — whether or not it
 * produced a job.
 *
 * `startEngineJob` races an unfiltered `CREATE_ENGINE_JOB_SUCCESS`, so with
 * several files uploading at once the id check is what keeps one upload from
 * consuming another's signal.
 *
 * NO JOB IS ALSO AN ANSWER. A PDF upload with both cognition stages unticked
 * skips the converter and has nothing left to run, so ingestion reports success
 * with a null `createJob` — which has no `targetId` to match on, so this waited
 * out the full timeout for a job that was never coming and the file stayed out
 * of the folder for ten seconds. The id comes off `meta.variables` in that
 * case; see the `tasks.length === 0` branch in the ingestion service.
 */
const isIngestionAnswerForTdo = (tdoId: string) => (action: unknown) => {
  if (!CREATE_ENGINE_JOB_SUCCESS.match(action)) {
    return false;
  }
  const job = action.payload?.createJob;
  return job
    ? job.targetId === tdoId
    : (action.meta as { variables?: { id?: string } } | undefined)?.variables
        ?.id === tdoId;
};

/**
 * Re-fetch the selected folder whether or not it lists the file.
 * `refreshSelectedFolderForTdo` no-ops when the folder does not list it, which
 * is exactly the fresh-upload case. Call it only once the fast path has written
 * `recorded` AND the ingestion job exists, so the row comes back on the
 * server's word with its job records attached — see `handleCreateTDOSuccess`.
 */
export function* refreshSelectedFolder() {
  const selectedVirtualFolder: VirtualFolderStateType = yield* select(
    selectSelectedVirtualFolder
  );
  if (selectedVirtualFolder.parent) {
    yield* put(
      actionSetSelectedFolder({
        folder: selectedVirtualFolder.parent,
        isCase: true,
      })
    );
  }
}

function* onActionCreateTDO() {
  yield* takeEvery(
    CREATE_TDO,
    function* ({
      payload,
      meta: {
        govQARequestId,
        foiaXpressRequestId,
        casepointRequestId,
        nuixRequestId,
        exterroRequestId,
        callback,
        caseId,
        settingsProfileId,
      },
    }) {
      let settingsProfile: GlobalSettings | undefined = undefined;
      if (settingsProfileId) {
        const allProfiles = yield* select(selectProfileList);
        const selectedProfile = (allProfiles || [])?.find(
          (p) => p.id === settingsProfileId
        );
        if (selectedProfile?.data?.settings) {
          settingsProfile = {
            ...selectedProfile?.data.settings,
            isSaved: true,
          };
        }
      }
      yield* put(
        serviceCreateTDO({
          ...payload,
          fileUrl: payload.getUrl,
          govQARequestId,
          foiaXpressRequestId,
          casepointRequestId,
          nuixRequestId,
          exterroRequestId,
          caseId,
          settingsProfile,
        })
      );
      if (callback) {
        const { ok } = yield* race({
          ok: take(CREATE_TDO_SUCCESS),
          err: take(CREATE_TDO_FAILURE),
        });

        if (ok) {
          callback();
        }
      }
    }
  );
}

function* onActionCreateTDOSuccess() {
  yield* takeEvery(CREATE_TDO_SUCCESS, handleCreateTDOSuccess);
}

/**
 * Exported so the ordering it enforces can be driven in a test: the folder
 * re-fetch must come after `processEngineRequestAction`, never before.
 */
export function* handleCreateTDOSuccess({
  payload,
  meta,
}: ReturnType<typeof CREATE_TDO_SUCCESS>) {
  if (meta?.variables.error) {
    return;
  }
  const featureFlags = yield* select(selectFeatureFlags);
  let tookPdfFastPath = false;
  // VE-30513: set when `attachOriginalMedia` confirms our write has landed
  // on the server. The engine payload omits `url` only in that case — see
  // the `ProcessEngineRequest` build further down. Default `false` keeps
  // every other path (PDF fast upload, Ingest-from-URL, kill switch on,
  // confirm-or-fail miss) sending `url` as before.
  let appOwnsOriginalMedia = false;

  // VE-23391: flag every freshly-uploaded file with a "New" pill in the grid
  // (session-scoped). Independent of the fast-PDF path below.
  yield* put(actionMarkOrgTdoNew(payload.createTDO.id));

  const fileType = payload.createTDO.details.veritoneFile?.fileType;

  // VE-23391: PDFs skip the converter engine. Attach the uploaded file as the
  // `media` asset, fill in veritoneFile + page count, and flip to `recorded`
  // before kicking off the (now converter-less) engine request — nothing else
  // writes that status for a converter-less TDO, so this is what makes the
  // file openable at all. The unsigned URL is preferred for the asset
  // reference (it doesn't expire); the signed getUrl reads the page count now.
  if (
    !featureFlags.disableFastPdfIngestion &&
    isPdfMedia(fileType, payload.createTDO.name)
  ) {
    try {
      // The asset's `uri` is PERMANENT, so it must be the unsigned one.
      // Falling back to the signed `getUrl` here used to look harmless and
      // wasn't: that URL dies after 12 hours (`getSignedWritableUrl` is
      // requested with `expiresInSeconds: 43200`), so the document opened all
      // day and then stopped opening the next, with nothing failing in
      // between. Refuse instead — the catch below surfaces it and the user can
      // retry, which beats succeeding into a time bomb.
      if (!meta.variables.unsignedUrl) {
        throw new Error('upload produced no unsigned URL for the media asset');
      }
      yield* call(setupPdfFastUpload, {
        tdoId: payload.createTDO.id,
        fileName: payload.createTDO.name,
        uri: meta.variables.unsignedUrl,
        pageCountUrl: meta.variables.url,
      });
      // `serviceFinalizePdfTdo` has just written `recorded` on the
      // server, so mirror it into the grid's TDO record. Nothing else will for
      // a long while — the next detail fetch fires on
      // PROCESS_ENGINE_REQUEST_SUCCESS, which the ingestion saga dispatches at
      // the END of `startPollingJobStatus`, i.e. only once the whole job
      // completes. Until this lands the tile reads "Processing New Media" and
      // its filename carries `pointer-events: none`, so a file that is in fact
      // openable cannot be clicked.
      // Both dashboards keep their own copy of the file, and both gate
      // openability on its `status` — so both need mirroring.
      yield* put(actionMarkOrgTdoRecorded(payload.createTDO.id));
      yield* put(actionMarkTdoRecorded(payload.createTDO.id));
      tookPdfFastPath = true;
    } catch (e) {
      console.error('[uploads] PDF fast-upload setup failed', e);
      yield* put(
        enqueueSnackbar({
          message: 'Could not prepare the PDF for redaction. Try again.',
        })
      );
      // Stop rather than fall through to the engine request. This
      // path only creates an asset and updates the TDO, so a failure means the
      // platform refused one of those — the converter would hit the same wall.
      return;
    }
  } else if (isDocumentFile(fileType, payload.createTDO.name)) {
    /**
     * Keep the file the user uploaded, since it is NOT what we will open.
     *
     * This branch is everything the fast path did not take: a non-PDF bound
     * for the converter, and a PDF forced through it by
     * `disableFastPdfIngestion`. In both cases the `media` asset ends up being
     * the converter's output, and without this the user's own file is an
     * unreferenced blob in S3 that no download can reach.
     *
     * BEST EFFORT and `url` IS KEPT in the engine payload (unlike the
     * VE-30513 branch below). Documents go through the PDF-converter → OCR →
     * PII pipeline (`services.ts:566-574`), which reads `payload.url`
     * directly as the input to the first engine — a dropped URL would leave
     * the converter with no source. A failure here costs a convenience
     * download, not the document, so it warns and carries on, unlike the
     * fast-path branch above, which returns because its failure means there
     * is nothing to open.
     */
    try {
      if (!meta.variables.unsignedUrl) {
        throw new Error('upload produced no unsigned URL');
      }
      const token = yield* select(selectToken);
      const cfg = yield* select(selectGraphQLConfig);
      yield* call(
        serviceCreateOriginalMediaAsset,
        payload.createTDO.id,
        meta.variables.unsignedUrl,
        fileType ?? 'application/octet-stream',
        payload.createTDO.name,
        token,
        cfg
      );
    } catch (e) {
      console.warn(
        '[uploads] could not keep the original-format file; the converted PDF will be the only download',
        e
      );
    }
  } else if (shouldAttachOriginalMedia(fileType, payload.createTDO.name)) {
    /**
     * VE-30513: the app owns the `original-media` write for A/V, image, and
     * the browser's untyped `binary/octet-stream` fallback (for files it
     * could not sniff — `.mkv`/`.mxf`/`.m2ts`/…).
     *
     * HISTORICAL CONTEXT. The GLC ingester used to write this asset by
     * substring-matching the media URL against AWS and Azure patterns, which
     * went silent on Oracle Object Storage once storage migrated. The
     * revised design (per Derrick on PR #4006): the app is the single source
     * of truth on every backend, and the ingester's own URL is dropped from
     * the engine payload (see the engine-request builder further down) so
     * the signed URL does not persist on the queryable `Task.payload` /
     * `Task.payloadString` fields for 12 hours. The ingester falls back to
     * this asset via `glc-ingestor/ingestion.py:1268-1286` — no engine
     * change needed, which is why the mechanism is implicit (no `assetId`
     * payload field anywhere in the ingester) and this comment exists.
     *
     * CONFIRM-OR-FAIL rather than best-effort. The engine now depends on
     * our asset existing when `url` is dropped: a silent failure succeeds
     * into a time-bomb (job created with no `url` and no asset, task dies
     * inside the engine minutes later). `attachOriginalMedia` races the
     * write against a 10 s timeout, reads back to disambiguate a timed-out
     * fetch (`race` cancels the saga branch but NOT the in-flight fetch, and
     * `createAsset` is not idempotent), and retries once.
     *
     * KILL SWITCH placement. `featureFlags.disableOriginalMediaAttach` is
     * read inside the branch body, NOT in the `else if` condition, and the
     * two placements are not interchangeable. `disableFastPdfIngestion`
     * belongs in its condition because its fall-through is the point: a
     * disabled fast-PDF is meant to drop to the DOCUMENT branch below it
     * (`application/pdf` is in `DOCUMENT_REDACTION_MIME_TYPES` and `.pdf` in
     * `DOCUMENT_REDACTION_EXTENSIONS`, so `isDocumentFile` claims it) and
     * ingest through the converter. This flag has nowhere to fall through
     * TO — turning it off means "skip the attach", not "try another
     * branch" — so a condition-level check would be a no-op today and an
     * outright bug the moment a fourth `else if` is appended, silently
     * routing disabled uploads into it. The body-level check keeps the
     * branch terminal whatever the flag says.
     */
    if (featureFlags.disableOriginalMediaAttach) {
      // Kill switch on: skip the app-side attach and keep `url` in the
      // engine payload so the ingester's own creation path handles it. This
      // is the rollback lane — if the ingester-side fix lands and starts
      // misbehaving (duplicate records, misclassification, …) ops can flip
      // this per-org without a Redact deploy.
    } else if (!meta.variables.unsignedUrl) {
      // No permanent URL to attach, so behave exactly as the kill switch
      // does: skip the attach and leave `url` in the engine payload, which
      // still ingests the file via the ingester's own writer.
      //
      // Unreachable today — `overview/saga.ts` only dispatches CREATE_TDO
      // for files passing `hasGetUrl`, and the upload result nulls `getUrl`
      // and `unsignedUrl` together on error, so anything arriving here has
      // both. It is written as a DEGRADE rather than a hard failure on
      // purpose: if that invariant ever breaks, refusing to ingest a file
      // that uploaded successfully is a worse outcome for the user than one
      // signed URL landing on a task payload.
      console.warn(
        '[uploads] no unsigned URL for an upload that should have one; skipping the original-media attach and keeping `url`'
      );
    } else {
      const token = yield* select(selectToken);
      const cfg = yield* select(selectGraphQLConfig);
      const confirmed = yield* attachOriginalMedia({
        tdoId: payload.createTDO.id,
        uri: meta.variables.unsignedUrl,
        contentType: fileType ?? 'application/octet-stream',
        fileName: payload.createTDO.name,
        token,
        cfg,
      });
      if (!confirmed) {
        // Do NOT dispatch the engine request. A job created with no `url`
        // (we drop it below) and no `original-media` asset is accepted by
        // job creation and dies inside the engine with
        // `IngestionError("payload url is null/None")`, surfacing as a tile
        // that fails minutes later rather than an immediate error. See
        // `attachOriginalMedia`.
        yield* put(
          enqueueSnackbar({
            message: 'Could not prepare the upload. Try again.',
            variant: 'error',
          })
        );
        return;
      }
      appOwnsOriginalMedia = true;
    }
  }

  // Custom lexicons (VE-24948 / U5, `U5-FR14`): record the upload dialog's
  // selection against the new document — never evaluate on this path
  // (BR-49). Dispatched, not called: the write targets the
  // `document-redaction-data` asset the DOCUMENTS module owns, along with
  // its history rotation, so that module owns the write and its failure
  // surface (review-gate M11). Fire-and-forget by design — lexicons are
  // additive and must not gate an upload.
  if (isPdfMedia(fileType, payload.createTDO.name)) {
    // The upload URL keys this file's share of the staged lexicon batch —
    // `url` here is the same `getUrl` the overview saga staged.
    yield* put(
      RECORD_UPLOAD_LEXICON_SELECTION({
        tdoId: payload.createTDO.id,
        fileUrl: meta.variables.url ?? '',
      })
    );
  }

  let urlHeaders: string | undefined = undefined;
  if (meta?.variables?.input?.details?.foiaXpressRequestId) {
    yield* put(foiaXpressAPI.fetchIntegrationInfo());
    const { ok, err } = yield* race({
      ok: take(foiaXpressAPI.FETCH_INTEGRATION_INFO_SUCCESS),
      err: take(foiaXpressAPI.FETCH_INTEGRATION_INFO_FAILURE),
    });
    if (err) {
      yield* put(
        enqueueSnackbar({
          message: 'Could not fetch FOIAXpress Integration User.',
        })
      );
      return;
    }

    const {
      INGESTION_USERNAME: integrationUsername,
      INGESTION_PASSWORD: integrationPassword,
    } = ok?.payload?.me?.organization?.integrationConfig?.config || {};

    if (integrationUsername && integrationPassword) {
      try {
        const encodedValue = btoa(
          `${integrationUsername}:${integrationPassword}`
        );
        const headers = { Authorization: `Basic ${encodedValue}` };
        urlHeaders = JSON.stringify(headers);
      } catch (_e) {
        console.error('failed to create urlHeaders - base 64 encoding error');
      }
    }
  }

  const req: ProcessEngineRequest = {
    tdoId: payload.createTDO.id,
    tdoName: payload.createTDO.name,
    payload: {
      tdoId: payload.createTDO.id,
      // VE-30513: omit `url` when the app has confirmed its own
      // `original-media` write. The signed `getUrl` would otherwise persist
      // on the task record's `Task.payload` and `Task.payloadString`
      // queryable fields — a 12 h bearer capability to the unredacted
      // original (`getSignedWritableUrl` is requested with
      // `expiresInSeconds: 43200`) sitting in a durable queryable field.
      // The ingester reads the TDO's `original-media` asset as its fallback
      // source (`glc-ingestor/ingestion.py:1268-1286`), so the engine still
      // gets a fetchable URL. The mechanism is implicit — there is no
      // `assetId` payload field anywhere in the ingester — so do NOT add
      // `url` back here as a "simplification".
      ...(appOwnsOriginalMedia ? {} : { url: meta.variables.url }),
      ...(urlHeaders && {
        urlHeaders,
      }),
      fileType,
      faceDetectionThreshold:
        payload.createTDO.details?.settings?.faceDetectionThreshold,
      // Only if they are defined
      ...(featureFlags.devLegacyCluster &&
        payload.createTDO.details?.settings?.legacyClustering && {
          legacyClustering:
            payload.createTDO.details?.settings?.legacyClustering,
        }),
      // Note: only check legacyClustering boolean if the legacy cluster featureFlag is not
      ...((!featureFlags.devLegacyCluster ||
        payload.createTDO.details?.settings?.legacyClustering === false) &&
        payload.createTDO.details?.settings?.clusterSimThreshold && {
          clusterSimThreshold:
            payload.createTDO.details?.settings?.clusterSimThreshold,
        }),
    },
  };
  yield* put(processEngineRequestAction(req));

  if (tookPdfFastPath) {
    // Wait for the ingestion request to be ANSWERED before re-fetching. A fresh
    // upload is not in the folder yet, so this fetch is what puts it there — and
    // a fetch issued before the job exists returns `recorded` with no
    // `jobs.records`, which `getFileProcessingStatus` reads as `complete`. That
    // is the window the deleted `cognitionPending` flag used to cover; ordering
    // covers it now.
    //
    // "Answered" rather than "a job was created": with both cognition stages
    // unticked there is no job at all, and waiting for one held the row back for
    // the full timeout on the fastest upload the app has. Bounded regardless,
    // because a failed create retries for far longer than it is worth
    // withholding the row.
    yield* race({
      answered: take(isIngestionAnswerForTdo(payload.createTDO.id)),
      timeout: delay(JOB_CREATE_WAIT_MS),
    });
    yield* call(refreshSelectedFolder);
  }
}

function* onActionCreateTDOFailure() {
  yield* takeEvery(CREATE_TDO_FAILURE, function* () {
    yield* put(
      enqueueSnackbar({
        message: 'Error! The media file was not added to the application.',
      })
    );
  });
}

function* onActionIngestionComplete() {
  yield* takeEvery(PROCESS_ENGINE_REQUEST_SUCCESS, handleIngestionComplete);
}

export function* handleIngestionComplete({
  payload,
}: {
  payload: { tdoId: TDOId };
}) {
  yield* call(refreshSelectedFolderForTdo, payload.tdoId);
  yield* delay(10_000);
  yield* put(actionFetchOrgTDODetail(payload));
}

function* onActionIngestionFailure() {
  yield* takeEvery(PROCESS_ENGINE_REQUEST_FAILURE, function* () {
    yield* put(
      enqueueSnackbar({
        message: 'Processing new media failed. Try uploading again.',
      })
    );
  });
}
